import { useMemo } from 'react';
import type { Appointment } from '@/types';
import type { PatientWithDoctors } from '@/services/api/patients.service';

interface CalendarWeekViewProps {
  startDate: Date;
  appointments: Appointment[];
  patients: PatientWithDoctors[];
  onAppointmentClick: (appointment: Appointment) => void;
  onTimeSlotClick: (date: Date, startTime: Date) => void;
}

export function CalendarWeekView({
  startDate,
  appointments,
  patients,
  onAppointmentClick,
  onTimeSlotClick,
}: CalendarWeekViewProps) {
  const weekDays = useMemo(() => {
    const days = [];
    const start = new Date(startDate);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);

    for (let i = 0; i < 5; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push(date);
    }
    return days;
  }, [startDate]);

  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 7; hour <= 19; hour++) {
      slots.push(hour);
    }
    return slots;
  }, []);

  const getPatientName = (patientId?: string | null) => {
    if (!patientId) return 'Sem paciente';
    const patient = patients.find((p) => p.id === patientId);
    return patient?.fullName || 'N/A';
  };

  const getAppointmentsForDayAndHour = (date: Date, hour: number) => {
    return appointments.filter((apt) => {
      const aptStart = new Date(apt.startUtc);
      const aptDate = new Date(aptStart);
      aptDate.setHours(0, 0, 0, 0);
      const targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);

      return (
        aptDate.getTime() === targetDate.getTime() &&
        aptStart.getHours() === hour
      );
    });
  };

  const formatTime = (hour: number) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('pt-BR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Realizada':
        return 'bg-green-100 border-green-500 hover:bg-green-200 text-green-900';
      case 'Cancelada':
        return 'bg-red-100 border-red-500 hover:bg-red-200 text-red-900';
      case 'Agendada':
      default:
        return 'bg-blue-100 border-blue-500 hover:bg-blue-200 text-blue-900';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="grid grid-cols-6 border-b">
        <div className="bg-gray-50 px-2 py-3 border-r">
          <span className="text-xs font-medium text-gray-500">Horário</span>
        </div>
        {weekDays.map((day, index) => (
          <div
            key={index}
            className="bg-gray-50 px-2 py-3 border-r last:border-r-0 text-center"
          >
            <div className="text-sm font-semibold text-gray-900">
              {formatDate(day)}
            </div>
          </div>
        ))}
      </div>

      {/* Time Grid */}
      <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
        {timeSlots.map((hour) => (
          <div key={hour} className="grid grid-cols-6 border-b">
            {/* Time Column */}
            <div className="bg-gray-50 px-2 py-2 border-r">
              <span className="text-xs text-gray-500">{formatTime(hour)}</span>
            </div>

            {/* Day Columns */}
            {weekDays.map((day, dayIndex) => {
              const dayAppointments = getAppointmentsForDayAndHour(day, hour);

              return (
                <div
                  key={dayIndex}
                  className="relative border-r last:border-r-0 p-1 hover:bg-gray-50 cursor-pointer transition-colors group min-h-[60px]"
                  onClick={() => {
                    const clickDate = new Date(day);
                    clickDate.setHours(hour, 0, 0, 0);
                    onTimeSlotClick(day, clickDate);
                  }}
                >
                  {/* Appointments */}
                  {dayAppointments.map((appointment) => (
                    <div
                      key={appointment.id}
                      className={`mb-1 border-l-2 rounded px-1 py-1 transition-colors cursor-pointer text-xs ${getStatusColor(appointment.status)}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAppointmentClick(appointment);
                      }}
                    >
                      <div className="font-semibold truncate">
                        {appointment.title}
                      </div>
                      <div className="truncate opacity-80">
                        {getPatientName(appointment.patientId)}
                      </div>
                    </div>
                  ))}

                  {/* New Appointment Hint */}
                  {dayAppointments.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      <span className="text-xs text-gray-400">+</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
